/* File: src/contexts/EventContext.js */
import React, { createContext, useState, useEffect, useContext } from 'react';
import { publicApi } from '../services/api';

// Création du contexte
const EventContext = createContext();

export const useEvent = () => {
  const context = useContext(EventContext);
  if (!context) {
    console.warn('useEvent must be used within an EventProvider');
    return {
      eventId: null,
      eventDetails: null,
      loading: false,
      error: null,
      refreshEventDetails: () => {},
      userAgeVerified: false
    };
  }
  return context;
};

export const EventProvider = ({ children }) => {
  const [eventId, setEventId] = useState(null);
  const [eventDetails, setEventDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Vérifier si l'utilisateur a validé son âge
  const userAgeVerified = !!localStorage.getItem('birthYear');
  
  // Fonction pour créer un événement par défaut
  const createDefaultEvent = () => {
    return {
      id: '1',
      event_id: '1',
      name: 'Guinness Bright Night 2025',
      event_name: 'Guinness Bright Night 2025',
      date: '2025-12-13 20:00:00',
      event_date: '2025-12-13 20:00:00',
      location: 'Esplanade Stade Omnisports, Yaoundé',
      city: 'Yaoundé',
      description: 'La plus grande soirée de fin d\'année au Cameroun',
      visual_url: 'flyer-bright-night-2025.jpg',
      visuel: 'flyer-bright-night-2025.jpg'
    };
  };
  
  // Fonction pour obtenir l'événement le plus récent (BRIGHT NIGHT)
  const fetchLatestEvent = async () => {
    if (!userAgeVerified) {
      console.log('User age not verified, skipping event fetch');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      console.log('Fetching latest event (Bright Night)');
      
      const response = await publicApi.getLatestEvent();
      
      // ✅ CORRECTION : Vérifier response.data.data.event (double data)
      if (response?.data?.data?.event) {
        const latestEvent = response.data.data.event;
        const latestEventId = latestEvent.id || latestEvent.event_id;
        
        console.log('Event loaded from API:', latestEvent);
        
        // Sauvegarder l'ID de l'événement dans localStorage
        localStorage.setItem('currentEventId', latestEventId.toString());
        
        // Mettre à jour l'état
        setEventId(latestEventId.toString());
        setEventDetails(latestEvent);
        setError(null);
      } else {
        throw new Error('Format de réponse invalide');
      }
    } catch (apiError) {
      console.error('API Error, using fallback event:', apiError);
      
      // Utiliser un événement par défaut
      const fallbackEvent = createDefaultEvent();
      setEventId(fallbackEvent.id);
      setEventDetails(fallbackEvent);
      localStorage.setItem('currentEventId', fallbackEvent.id);
      setError('Utilisation des données en cache');
    } finally {
      setLoading(false);
    }
  };
  
  // Fonction pour charger les détails d'un événement spécifique
  const fetchEventDetails = async (id) => {
    if (!id || !userAgeVerified) {
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      console.log('Fetching event details for ID:', id);
      
      const response = await publicApi.getEventById(id);
      
      // ✅ CORRECTION : Vérifier response.data.data.event (double data)
      if (response?.data?.data?.event) {
        setEventDetails(response.data.data.event);
        setError(null);
        console.log('Event details loaded:', response.data.data.event);
      } else {
        throw new Error('Format de réponse invalide');
      }
    } catch (err) {
      console.error('Error loading event details:', err);
      setError('Une erreur est survenue lors du chargement des détails de l\'événement');
      
      // Utiliser un événement par défaut
      const fallbackEvent = createDefaultEvent();
      setEventDetails(fallbackEvent);
    } finally {
      setLoading(false);
    }
  };
  
  // Effet initial pour charger l'événement
  useEffect(() => {
    if (userAgeVerified && !eventDetails) {
      const timer = setTimeout(() => {
        fetchLatestEvent();
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [userAgeVerified]);
  
  // Effet secondaire pour charger les détails quand l'ID change
  useEffect(() => {
    if (eventId && userAgeVerified && !eventDetails) {
      const timer = setTimeout(() => {
        fetchEventDetails(eventId);
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [eventId, userAgeVerified]);
  
  // Surveiller les changements dans localStorage
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'currentEventId' && e.newValue !== eventId) {
        setEventId(e.newValue);
      }
      
      // Vérifier si birthYear a été ajouté
      if (e.key === 'birthYear' && e.newValue && !userAgeVerified) {
        fetchLatestEvent();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [eventId, userAgeVerified]);
  
  const value = {
    eventId,
    eventDetails,
    loading,
    error,
    refreshEventDetails: fetchLatestEvent,
    userAgeVerified
  };
  
  return (
    <EventContext.Provider value={value}>
      {children}
    </EventContext.Provider>
  );
};

export default EventContext;